import {
  createSlice,
  createAsyncThunk,
  miniSerializeError,
  SerializedError,
} from "@reduxjs/toolkit";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import { hasValidPermissions } from "../constants/constants";
import { Init as initAuth } from "../config/Auth";
import authService from "src/services/authService";
export type UserState = {
  token: string;
  alias: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  userLoading: "pending" | "fulfilled" | "rejected";
  authError: SerializedError | null;
  hasPermission: boolean;
  pageLastLoadTimestamp: number;
  isLoading: boolean;
};

const initialState: UserState = {
  token: "",
  alias: "",
  lastName: "",
  firstName: "",
  email: "",
  ldap: [],
  userLoading: "pending",
  authError: null,
  hasPermission: true,
  pageLastLoadTimestamp: 0,
  isLoading: false,
};

/**
 * get the authenticated user
 * @returns {Object}
 */
export const getUser = createAsyncThunk("user/getUser", async () => {
  return await authService.login();
});

/**
 * Sign in using federated authentication
 */
export const signIn = createAsyncThunk("user/signIn", async () => {
  await authService.federatedSignIn();
});

/**
 * Sign out the current user
 */
export const signOut = createAsyncThunk("user/signOut", async () => {
  await Auth.signOut();
});

/**
 * Initialize auth and attempt to get the current user
 */
export const initializeAuth = createAsyncThunk(
  "user/initializeAuth",
  async (_, { dispatch }) => {
    try {
      await initAuth();
      try {
        await dispatch(getUser()).unwrap();
        return true;
      } catch (error) {
        console.log("User not authenticated, will show sign-in option");
        return false;
      }
    } catch (error) {
      console.error("Failed to initialize authentication:", error);
      throw error;
    }
  }
);

const { reducer, actions } = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.authError = null;
    },
    setPageLastLoadTimestamp: (state) => {
      state.pageLastLoadTimestamp = Date.now();
    },
  },
  extraReducers: (builder) => {
    // Get User
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = "pending";
      state.isLoading = true;
      state.authError = null;
    });
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, ldap } = payload;
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.ldap = ldap;
      state.userLoading = "fulfilled";
      state.isLoading = false;
      state.authError = null;
      state.hasPermission = hasValidPermissions(ldap);
      state.pageLastLoadTimestamp = Date.now();

      if (state.ldap.length === 0) {
        state.hasPermission = false;
      }
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.userLoading = "rejected";
      state.isLoading = false;
      state.authError = miniSerializeError(error);
      state.hasPermission = false;
      state.token = "";
      state.alias = "";
      state.email = "";
      state.firstName = "";
      state.lastName = "";
      state.ldap = [];
    });

    // Sign In
    builder.addCase(signIn.pending, (state) => {
      state.isLoading = true;
      state.authError = null;
    });
    builder.addCase(signIn.fulfilled, (state) => {
      // Sign in success will trigger getUser
      state.isLoading = false;
    });
    builder.addCase(signIn.rejected, (state, { error }) => {
      state.isLoading = false;
      state.authError = miniSerializeError(error);
    });

    // Sign Out
    builder.addCase(signOut.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(signOut.fulfilled, (state) => {
      state.userLoading = "pending";
      state.isLoading = false;
      state.authError = null;
      state.token = "";
      state.alias = "";
      state.email = "";
      state.firstName = "";
      state.lastName = "";
      state.ldap = [];
      state.hasPermission = true;
    });
    builder.addCase(signOut.rejected, (state, { error }) => {
      state.isLoading = false;
      state.authError = miniSerializeError(error);
    });

    // Initialize Auth
    builder.addCase(initializeAuth.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(initializeAuth.fulfilled, (state) => {
      // Auth initialized, getUser has been dispatched already
      // so we don't need to update any state here
    });
    builder.addCase(initializeAuth.rejected, (state, { error }) => {
      state.isLoading = false;
      state.authError = miniSerializeError(error);
    });
  },
});

export const { clearAuthError, setPageLastLoadTimestamp } = actions;

export default reducer;
